import { useCallback, useMemo, useRef } from 'react';

import { StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';
import { BodySmall } from '@zola/zola-ui/src/typography/Paragraphs';

import styled from '@emotion/styled';
import Sticky from 'react-stickynode';

import { PreAuthVendorCard } from '~/components/common/cards/PreAuthVendorCard/PreAuthVendorCard';
import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { isSearchLocation } from '~/util/searchUtils';
import TextUtils from '~/util/textUtils';

import { isQuizCardView } from '../../../VendorCardGrid/utils';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorGridCards } from '../../contexts/VendorGridCardsContext';
import { SearchFacetAccordion } from '../SearchControls/facets/FacetSideDrawer/FacetSideDrawer';
import { useSearchCategories } from '../SearchControls/useSearchCategories';
import { AppliedFilters } from './AppliedFilters';
import { getCategoryHeading } from './PageHeading';

const Grid = styled.section`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 40px;
  padding: 0 40px;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const DesktopFilters = styled.div`
  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: none;
  }
  background-color: ${COLORS3.WHITE_100};
`;

const VendorList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

const GridHeader = styled.div`
  display: none;

  ${MEDIA_QUERY_V2.DESKTOP} {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0;
  }
`;

const GridRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SubHeading = () => {
  const {
    vendorSearch,
    searchResults: { totalHits },
  } = useSearchResultsPageContext();

  const { selectedTaxonomy, selectedLocation } = vendorSearch;

  const formattedCount = useMemo(() => {
    const maxShown = selectedTaxonomy || selectedLocation ? 1000 : 10000;

    const numShown = Math.min(maxShown, totalHits);
    let count = TextUtils.formatWithCommas(numShown);

    if (numShown === maxShown) {
      count = `${maxShown}+`;
    }
    return count;
  }, [selectedLocation, selectedTaxonomy, totalHits]);

  const formattedTitle = useMemo(() => {
    const description = `Wedding ${getCategoryHeading(selectedTaxonomy?.key)}`;
    const location = isSearchLocation(selectedLocation) ? selectedLocation.label : undefined;
    let title = description.trim();
    if (location) {
      title = `${title} in ${location}`;
    }
    return title;
  }, [selectedLocation, selectedTaxonomy]);

  return (
    <BodySmall>
      {formattedCount} {formattedTitle}
    </BodySmall>
  );
};

const SortControl = () => {
  return <div>Sort</div>;
};

export const PreAuthSearchResults = (): JSX.Element => {
  const { vendorSearch, loading } = useSearchResultsPageContext();
  const { vendorOrQuizCardViews: searchResults } = useVendorGridCards();

  const allFacetCategories = useSearchCategories(vendorSearch);

  const { availabilityDates, selectedLocation } = vendorSearch;
  const onlyVendorCards = searchResults.filter(
    (card) => !isQuizCardView(card)
  ) as StorefrontCardView[];

  // @see https://github.com/yahoo/react-stickynode/issues/102
  const sidebarDomRef = useRef(null); // needed for resize observer
  const stickyRef = useRef<any>(null); // needed to access sticky instance
  const sidebarResizeObserverRef = useRef<any>({}); // needed to remove observer when component is unloaded

  //  update sticky instance when height changes
  // @see https://github.com/yahoo/react-stickynode/issues/102
  const updateSticky = useCallback(() => {
    const stickyInstance = stickyRef.current;

    if (stickyInstance === null) {
      return;
    }

    stickyInstance.updateInitialDimension();
    stickyInstance.update();
  }, []);

  // observe sidebar height changes
  // @see https://github.com/yahoo/react-stickynode/issues/102
  useEffectOnce(() => {
    const sidebarDomElement = sidebarDomRef.current;

    sidebarResizeObserverRef.current = new ResizeObserver((entries) => {
      // ignore other sidebar observers (not strictly neccessary)
      const sidebarObserverResults = entries.find(({ target }) => target === sidebarDomElement);

      if (typeof sidebarObserverResults === 'undefined') {
        return;
      }

      updateSticky();
    });

    if (sidebarDomElement === null) {
      return;
    }

    sidebarResizeObserverRef.current.observe(sidebarDomElement);

    // cleanup when components is unloaded
    return () => {
      sidebarResizeObserverRef.current.disconnect();
    };
  });

  return (
    <Grid id="grid">
      <DesktopFilters>
        <Sticky innerZ={20} top={133} bottomBoundary="#grid" ref={stickyRef}>
          <div ref={sidebarDomRef}>
            <SearchFacetAccordion categories={allFacetCategories} noBorder noSearch noPadding />
          </div>
        </Sticky>
      </DesktopFilters>

      <VendorList>
        <GridHeader>
          <GridRow>
            <SubHeading />
            <SortControl />
          </GridRow>

          <AppliedFilters />
        </GridHeader>

        {onlyVendorCards.map((searchResultCardView, index) => {
          return (
            <PreAuthVendorCard
              position={index + 1}
              key={searchResultCardView.storefrontUuid}
              vendor={cardViewToVendorCardVendor(searchResultCardView)}
              availabilityDates={availabilityDates}
              selectedLocation={selectedLocation}
              loading={loading}
            />
          );
        })}
      </VendorList>
    </Grid>
  );
};
